import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, ApiError } from '../api/client.js';
import PageHeader from '../components/PageHeader.jsx';
import LoadingState from '../components/LoadingState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { useToast } from '../context/ToastContext.jsx';

/**
 * Demand partner onboarding — the whole workflow, in the console.
 *
 * WHAT THIS REPLACES
 * ------------------
 * Thirteen of the twenty-six `demand_partners` columns had no UI at all, including every
 * onboarding declaration migration 039 added. The enable toggle PATCHed `{enabled: true}`, got
 * back a 409 listing what was missing, and showed it as a toast — while offering no field
 * anywhere to supply any of it. So every declaration on production was set with curl, and the
 * 409 functioned as a dead end rather than a prompt.
 *
 * THREE STAGES, BECAUSE THEY FAIL FOR DIFFERENT REASONS AND HAVE DIFFERENT OWNERS
 * -------------------------------------------------------------------------------
 *   1. Technical     — endpoint, formats, credentials, timeouts. An integrator's job.
 *   2. Verification  — does the endpoint actually answer OpenRTB? Pressed, not assumed.
 *   3. Commercial    — the declarations the money depends on. Usually someone else's job, and
 *                      under a different permission (see lib/permissions.js).
 *
 * Nothing here blocks SAVING. A draft with three fields filled in is a legitimate state and
 * always has been — `enabled` is false on create and the server gates activation, not storage.
 * What each panel shows is which stage a given gap blocks, which is the question the old toast
 * could not answer.
 */

const SEMANTICS = {
  payout_semantics: {
    label: 'Payout semantics',
    help: 'What the number in their reporting actually means. Getting this wrong is how a 3.6x discrepancy goes unnoticed for a month.',
    options: [
      ['gross_receivable', 'Gross receivable — before their fee'],
      ['net_receivable', 'Net receivable — what BidDigi actually collects'],
      ['advertiser_spend', 'Advertiser spend — what the buyer paid, including their margin'],
      ['unknown', 'Unknown — not yet asked'],
    ],
  },
  impression_semantics: {
    label: 'Impression semantics',
    help: 'What they count. Wins and delivered impressions differ by a factor of 30+ on some partners.',
    options: [
      ['delivered', 'Delivered impressions'],
      ['wins', 'Auction wins'],
      ['requests', 'Requests'],
      ['unknown', 'Unknown — not yet asked'],
    ],
  },
  delivery_signal: {
    label: 'Delivery signal',
    help: 'How BidDigi learns an ad actually rendered. Declaring burl while render confirmation is off names a mechanism and makes it impossible in the same breath.',
    options: [
      ['burl', 'burl — billing notice'],
      ['nurl', 'nurl — win notice (not delivery)'],
      ['render_pixel', 'Render pixel in the creative'],
      ['none', 'None — delivery is unverifiable for this partner'],
    ],
  },
};

const RECONCILIATION = [
  ['connected', 'Connected — a reporting connector returns data'],
  ['pending_setup', 'Pending setup — enabled while reporting is still being arranged'],
  ['none_available', 'None available — this partner has no reporting API, knowingly accepted'],
  ['not_applicable', 'Not applicable — disabled or test row'],
];

const INTEGRATION_STATES = [
  ['draft', 'Draft'], ['pilot', 'Pilot'], ['live', 'Live'], ['retired', 'Retired'],
];

/** Which reporting fields a connector type actually needs. Blank = no connector configured. */
const REPORTING_SHAPES = {
  '': [],
  admile_adxsaas: ['baseUrl', 'sspName', 'sspId', 'apiKey', 'dataCenter', 'sspEpId'],
  bidmaster360: ['baseUrl', 'id', 'secKey'],
  vidverto: ['baseUrl', 'username', 'auth', 'zone'],
  rtb_stack: ['baseUrl', 'client', 'apiKey'],
  bc_bidqon: ['base', 'platform', 'endpoint', 'token'],
  adokut_zonereports: ['base', 'login', 'password', 'zone'],
  adokut_ctv: ['base', 'token', 'endpoint'],
  adscholars_stats: ['base', 'clientKey', 'pubId', 'secretKey'],
};
const SECRET_FIELDS = new Set(['apiKey', 'secKey', 'auth', 'token', 'password', 'secretKey']);

const OUTCOME_CLASS = { pass: 'active', warn: 'paused', fail: 'error', not_run: 'neutral' };

function Field({ label, help, children }) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      {children}
      {help && <span className="card__hint" style={{ marginTop: '0.25rem', display: 'block' }}>{help}</span>}
    </label>
  );
}

export default function PartnerOnboardingPage() {
  const { code } = useParams();
  const toast = useToast();
  const [partner, setPartner] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [verifications, setVerifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(null);
  const [draft, setDraft] = useState({});
  const [reportingConfig, setReportingConfig] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [all, ready, checks] = await Promise.all([
        api.listDemandPartners(),
        api.demandPartnerReadiness(),
        api.partnerVerifications(code),
      ]);
      const row = all.find((p) => p.code === code);
      if (!row) throw new ApiError(`No demand partner with code "${code}".`, 404, null);
      setPartner(row);
      setReadiness((ready.partners || []).find((p) => p.code === code) || null);
      setVerifications(checks.verifications || []);
      setDraft({});
      // Secret values are redacted server-side before they reach this bundle. Blank means
      // "leave it alone"; typing replaces it. Never render a redaction marker into an input --
      // saving the form would write the literal placeholder back as the credential.
      setReportingConfig(Object.fromEntries(
        Object.entries(row.reporting_config || {}).filter(([k]) => !SECRET_FIELDS.has(k))
      ));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load this partner.');
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { load(); }, [load]);

  const value = (key) => (key in draft ? draft[key] : (partner ? partner[key] ?? '' : ''));
  const set = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value === '' ? null : e.target.value }));
  const dirty = useMemo(() => Object.keys(draft).length > 0, [draft]);

  async function save(fields, label) {
    const body = Object.fromEntries(Object.entries(draft).filter(([k]) => fields.includes(k)));
    if (Object.keys(body).length === 0) return;
    setSaving(label);
    try {
      // Numeric columns must go as numbers, not the strings an <input> yields.
      for (const k of ['margin_pct', 'timeout_ms', 'agreed_timeout_ms', 'max_qps', 'burst_qps', 'agreed_max_qps']) {
        if (k in body && body[k] !== null) body[k] = Number(body[k]);
      }
      await api.patchDemandPartner(code, body);
      toast.success(`${label} saved.`);
      await load();
    } catch (err) {
      const body2 = err instanceof ApiError ? err.body : null;
      const extra = Array.isArray(body2?.blockers) ? ` ${body2.blockers.join(' ')}` : '';
      toast.error(`${err instanceof ApiError ? err.message : 'Could not save.'}${extra}`);
    } finally {
      setSaving(null);
    }
  }

  async function saveReportingConfig() {
    setSaving('Reporting');
    try {
      const merged = Object.fromEntries(Object.entries(reportingConfig).filter(([, v]) => v !== '' && v !== null));
      await api.patchDemandPartner(code, {
        reporting_connector_type: value('reporting_connector_type') || null,
        reporting_config: merged,
      });
      toast.success('Reporting configuration saved.');
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save reporting configuration.');
    } finally { setSaving(null); }
  }

  async function runVerification() {
    setVerifying(true);
    try {
      const res = await api.verifyDemandPartner(code);
      const worst = res.results.some((r) => r.outcome === 'fail') ? 'error'
        : res.results.some((r) => r.outcome === 'warn') ? 'info' : 'success';
      toast[worst === 'error' ? 'error' : worst === 'info' ? 'info' : 'success'](
        res.results.map((r) => `${r.checkType}: ${r.outcome}`).join(' · ')
      );
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not run the verification.');
    } finally { setVerifying(false); }
  }

  async function toggleEnabled(next) {
    if (next && !window.confirm(`Enable ${code}? This starts sending it live bid requests.`)) return;
    setSaving('Activation');
    try {
      await api.patchDemandPartner(code, { enabled: next });
      toast.success(next ? `${code} is live.` : `${code} paused.`);
      await load();
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      const blockers = Array.isArray(body?.blockers) ? body.blockers : [];
      toast.error(`${err instanceof ApiError ? err.message : 'Could not change activation.'}${blockers.length ? ` — ${blockers[0]}` : ''}`);
      await load();
    } finally { setSaving(null); }
  }

  if (loading) return <div className="page"><PageHeader title="Partner onboarding" /><LoadingState label="Loading partner…" /></div>;
  if (error) return <div className="page"><PageHeader title="Partner onboarding" /><EmptyState title="Couldn't load this partner" message={error} /></div>;

  const connector = value('reporting_connector_type') || '';
  const reportingFields = REPORTING_SHAPES[connector] ?? [];
  const bidCheck = verifications.find((v) => v.checkType === 'bid_endpoint');
  const reportCheck = verifications.find((v) => v.checkType === 'reporting_endpoint');

  return (
    <div className="page">
      <PageHeader
        title={partner.name}
        subtitle={<>Onboarding for <code className="code-chip">{partner.code}</code> · {partner.connector_type} · {partner.enabled ? 'enabled' : 'disabled'}</>}
        actions={<Link className="btn" to="/demand-partners">All partners</Link>}
      />

      {/* -------------------------------------------------- stage 3 summary, shown first */}
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Activation</h2>
          <span className="card__hint">
            {partner.enabled ? 'Live and receiving bid requests.' : 'Not receiving traffic.'}
          </span>
        </div>
        {readiness && readiness.enableBlockers && readiness.enableBlockers.length > 0 ? (
          <>
            <p className="card__hint">
              This partner cannot be enabled yet. Each item below blocks <strong>activation only</strong> —
              everything on this page can still be saved as a draft.
            </p>
            <ul>
              {readiness.enableBlockers.map((b, i) => <li key={i} className="cell-muted">{b}</li>)}
            </ul>
          </>
        ) : (
          <p className="card__hint">All activation requirements are met.</p>
        )}
        <div className="button-row">
          <button
            className={`btn ${partner.enabled ? '' : 'btn--primary'}`}
            disabled={saving === 'Activation'}
            onClick={() => toggleEnabled(!partner.enabled)}
          >
            {saving === 'Activation' ? 'Saving…' : partner.enabled ? 'Pause partner' : 'Enable partner'}
          </button>
          <span className="cell-muted">
            Last verified: {partner.last_verified_at ? new Date(partner.last_verified_at).toISOString().slice(0, 16).replace('T', ' ') + 'Z' : 'never'}
          </span>
        </div>
      </section>

      {/* -------------------------------------------------- stage 2: verification */}
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Endpoint verification</h2>
          <span className="card__hint">
            Sends a real OpenRTB request with <code className="code-chip">test:1</code>, a
            $1,000,000 floor and a reserved <code className="code-chip">.invalid</code> bundle.
            It cannot produce a billable impression and is not recorded as an auction.
          </span>
        </div>
        <div className="button-row">
          <button className="btn btn--primary" disabled={verifying} onClick={runVerification}>
            {verifying ? 'Checking…' : 'Run verification'}
          </button>
        </div>
        {[bidCheck, reportCheck].filter(Boolean).map((v) => (
          <div key={v.checkType} className="card" style={{ marginTop: '0.75rem', padding: '0.75rem 1rem' }}>
            <div className="button-row">
              <span className={`status-badge status-badge--${OUTCOME_CLASS[v.outcome] || 'neutral'}`}>{v.outcome}</span>
              <strong>{v.checkType === 'bid_endpoint' ? 'Bid endpoint' : 'Reporting endpoint'}</strong>
              <span className="cell-muted">
                {new Date(v.ranAt).toISOString().slice(0, 16).replace('T', ' ')}Z
                {v.latencyMs !== null ? ` · ${v.latencyMs}ms` : ''}
                {v.runByType === 'api_key' ? ' · by an automation key' : v.runBy ? ` · by ${v.runBy}` : ''}
              </span>
            </div>
            <p style={{ margin: '0.5rem 0' }}>{v.summary}</p>
            <ul style={{ margin: 0 }}>
              {(v.checks || []).map((c, i) => (
                <li key={i} className="cell-muted">
                  {c.ok === true ? '✓' : c.ok === false ? '✗' : '–'} <strong>{c.name}</strong>: {c.detail}
                </li>
              ))}
            </ul>
            {v.responseSample && (
              <details style={{ marginTop: '0.5rem' }}>
                <summary className="cell-muted">What they sent back</summary>
                <pre className="code-chip" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{v.responseSample}</pre>
              </details>
            )}
          </div>
        ))}
        {verifications.length === 0 && (
          <p className="card__hint">
            Never checked. A partner that has not been verified is not the same as one that
            failed — neither is a reason to assume it works.
          </p>
        )}
      </section>

      {/* -------------------------------------------------- stage 1: technical */}
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Technical configuration</h2>
          <span className="card__hint">Does not block saving. Needed before verification can pass.</span>
        </div>
        <form className="form" onSubmit={(e) => { e.preventDefault(); save(['endpoint_url', 'timeout_ms', 'max_qps', 'burst_qps'], 'Technical configuration'); }}>
          <Field label="Bid endpoint URL" help="Where BidDigi POSTs the OpenRTB request.">
            <input value={value('endpoint_url') || ''} onChange={set('endpoint_url')} placeholder="https://partner.example/rtb/bid" />
          </Field>
          <Field label="Timeout (ms)" help="How long BidDigi waits for this partner before abandoning the call.">
            <input type="number" min="1" value={value('timeout_ms') || ''} onChange={set('timeout_ms')} />
          </Field>
          <Field label="Max QPS (operational)" help="Blank is uncapped. The contractual ceiling lives under Commercial terms.">
            <input type="number" min="1" value={value('max_qps') ?? ''} onChange={set('max_qps')} />
          </Field>
          <Field label="Burst" help="Bucket depth. Blank means one second of the sustained rate.">
            <input type="number" min="1" value={value('burst_qps') ?? ''} onChange={set('burst_qps')} />
          </Field>
          <div className="form__actions">
            <button type="submit" className="btn btn--primary" disabled={!dirty || saving === 'Technical configuration'}>
              {saving === 'Technical configuration' ? 'Saving…' : 'Save technical configuration'}
            </button>
          </div>
        </form>
      </section>

      {/* -------------------------------------------------- conditional: reporting */}
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Reporting integration</h2>
          <span className="card__hint">
            The fields below change with the connector — a partner is not asked for an
            <code className="code-chip">sspEpId</code> it does not have.
          </span>
        </div>
        <Field label="Reporting connector" help="How BidDigi pulls this partner's own figures for reconciliation.">
          <select value={connector} onChange={(e) => setDraft((d) => ({ ...d, reporting_connector_type: e.target.value || null }))}>
            <option value="">None — revenue from this partner cannot be reconciled</option>
            {Object.keys(REPORTING_SHAPES).filter(Boolean).map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </Field>
        {reportingFields.length > 0 && (
          <>
            {reportingFields.map((f) => (
              <Field
                key={f}
                label={f}
                help={SECRET_FIELDS.has(f) ? 'Stored write-only. Leave blank to keep the existing value.' : undefined}
              >
                <input
                  type={SECRET_FIELDS.has(f) ? 'password' : 'text'}
                  value={reportingConfig[f] ?? ''}
                  placeholder={SECRET_FIELDS.has(f) ? '(unchanged)' : ''}
                  onChange={(e) => setReportingConfig((c) => ({ ...c, [f]: e.target.value }))}
                />
              </Field>
            ))}
            <Field label="Reporting currency" help="Leave blank to fall back to the declared currency. Unknown currency means the figures cannot be used for payment.">
              <input value={reportingConfig.currency ?? ''} onChange={(e) => setReportingConfig((c) => ({ ...c, currency: e.target.value }))} placeholder="USD" />
            </Field>
          </>
        )}
        <div className="form__actions">
          <button className="btn btn--primary" disabled={saving === 'Reporting'} onClick={saveReportingConfig}>
            {saving === 'Reporting' ? 'Saving…' : 'Save reporting configuration'}
          </button>
        </div>
      </section>

      {/* -------------------------------------------------- stage 3: declarations */}
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Onboarding declarations</h2>
          <span className="card__hint">
            These block activation, not saving. Every defect in the 2026-09-12 revenue incident
            was one of these going unasked.
          </span>
        </div>
        <form className="form" onSubmit={(e) => {
          e.preventDefault();
          save(['payout_semantics', 'impression_semantics', 'delivery_signal', 'declared_currency',
            'agreed_timeout_ms', 'reconciliation_status', 'integration_state', 'onboarding_notes'], 'Declarations');
        }}>
          {Object.entries(SEMANTICS).map(([key, spec]) => (
            <Field key={key} label={spec.label} help={spec.help}>
              <select value={value(key) || ''} onChange={set(key)}>
                <option value="">Not declared</option>
                {spec.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
          ))}
          <Field label="Declared currency" help="The currency the partner says it bids and reports in. Three letters.">
            <input value={value('declared_currency') || ''} onChange={set('declared_currency')} placeholder="USD" maxLength={3} />
          </Field>
          <Field label="Agreed timeout (ms)" help="What the partner agreed to respond within, as distinct from what BidDigi currently waits.">
            <input type="number" min="1" value={value('agreed_timeout_ms') || ''} onChange={set('agreed_timeout_ms')} />
          </Field>
          <Field label="Reconciliation status" help="Whether revenue on this partner can be checked against their own reporting.">
            <select value={value('reconciliation_status') || ''} onChange={set('reconciliation_status')}>
              <option value="">Not set</option>
              {RECONCILIATION.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Lifecycle" help="Promotion from pilot to live is granted by a real reconciliation, not set here.">
            <select value={value('integration_state') || ''} onChange={set('integration_state')}>
              {INTEGRATION_STATES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </Field>
          <Field label="Notes" help="Anything a future operator would need — an unresolved question with the partner, a conflicting declaration.">
            <textarea rows={3} value={value('onboarding_notes') || ''} onChange={set('onboarding_notes')} />
          </Field>
          <div className="form__actions">
            <button type="submit" className="btn btn--primary" disabled={!dirty || saving === 'Declarations'}>
              {saving === 'Declarations' ? 'Saving…' : 'Save declarations'}
            </button>
          </div>
        </form>
      </section>

      {/* -------------------------------------------------- commercial, separately permissioned */}
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Commercial terms</h2>
          <span className="card__hint">
            Requires the <code className="code-chip">commercial.edit</code> permission — a 403
            here means your role does not cover money, which is deliberate.
          </span>
        </div>
        <form className="form" onSubmit={(e) => { e.preventDefault(); save(['margin_pct', 'agreed_max_qps', 'agreed_qps_source'], 'Commercial terms'); }}>
          <Field label="Margin (%)" help="BidDigi's cut, used for LIVE auction pricing. Historical restatement uses the dated rule in force on each day — see Commercial rules.">
            <input type="number" min="0" max="99.9" step="0.1" value={value('margin_pct') ?? ''} onChange={set('margin_pct')} />
          </Field>
          <Field label="Agreed max QPS (contractual)" help="What the partner agreed to RECEIVE. Max QPS above this is refused.">
            <input type="number" min="1" value={value('agreed_max_qps') ?? ''} onChange={set('agreed_max_qps')} />
          </Field>
          <Field label="Where that figure comes from" help="An IO clause, an email, a dated message. A cap with no provenance gets routed around.">
            <input value={value('agreed_qps_source') || ''} onChange={set('agreed_qps_source')} placeholder='e.g. "IO 2026-09-14 clause 4"' />
          </Field>
          <div className="form__actions">
            <button type="submit" className="btn btn--primary" disabled={!dirty || saving === 'Commercial terms'}>
              {saving === 'Commercial terms' ? 'Saving…' : 'Save commercial terms'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
